import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#removeLowQualityGalleryMedia();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
    requestAnimationFrame(() => this.#selectInitialVariantMedia());
  }

  #controller = new AbortController();

  #mediaSelectionRequest = 0;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by selecting the variant media when possible.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = async (event) => {
    const source = event.detail.data.html;
    const variantMediaId = event.detail.resource?.featured_media?.id;

    if (source) {
      const newMediaGallery = source.querySelector('media-gallery');

      if (newMediaGallery) {
        this.replaceWith(newMediaGallery);
        newMediaGallery.removeLowQualityMedia?.();
        if (variantMediaId) {
          this.#scheduleMediaSelection(newMediaGallery, String(variantMediaId));
        }
        return;
      }
    }

    if (variantMediaId && !this.#isLowQualityMediaId(String(variantMediaId))) {
      this.#selectMediaById(String(variantMediaId));
      this.#scheduleMediaSelection(this, String(variantMediaId));
    }
  };

  /**
   * Public hook used after this element is inserted with replaceWith().
   */
  removeLowQualityMedia() {
    this.#removeLowQualityGalleryMedia();
  }

  /**
   * Selects a media item by Shopify media id.
   * @param {string} mediaId - Shopify media ID.
   * @returns {boolean}
   */
  selectMediaById(mediaId) {
    return this.#selectMediaById(mediaId);
  }

  /**
   * Selects a media item by Shopify media id.
   * @param {string} mediaId - Shopify media ID.
   * @returns {boolean}
   */
  #selectMediaById(mediaId) {
    if (!mediaId) return false;
    if (this.#isLowQualityMediaId(mediaId)) return false;

    const slide = this.refs.slideshow?.refs.slides?.find((item) => item.getAttribute('slide-id') === mediaId);
    if (slide) {
      this.slideshow?.select({ id: mediaId }, undefined, { animate: false });
      this.#syncSelectedSlide(mediaId);
      return true;
    }

    const safeMediaId = globalThis.CSS?.escape ? CSS.escape(mediaId) : mediaId.replaceAll('"', '\\"');
    const media = this.querySelector(`[data-media-id="${safeMediaId}"]`);
    const mediaContainer = media?.closest('[ref="media[]"], .product-media-container, slideshow-slide');
    if (!mediaContainer) return false;

    mediaContainer.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    return true;
  }

  /**
   * Keeps the visible slide and thumbnails in sync when media is selected before
   * the slideshow internals finish reacting to a variant change.
   * @param {string} mediaId - Shopify media ID.
   */
  #syncSelectedSlide(mediaId) {
    this.#syncGalleryElementById(this, mediaId);
  }

  /**
   * Keeps an arbitrary gallery element in sync. Useful immediately after
   * replacing this element with Section Rendering API HTML.
   * @param {Element} gallery
   * @param {string} mediaId
   */
  #syncGalleryElementById(gallery, mediaId) {
    const slides = Array.from(gallery.querySelectorAll('slideshow-slide'));
    const selectedIndex = slides.findIndex((slide) => slide.getAttribute('slide-id') === mediaId);
    if (selectedIndex < 0) return;

    slides.forEach((slide) => {
      const isSelected = slide.getAttribute('slide-id') === mediaId;
      slide.setAttribute('aria-hidden', isSelected ? 'false' : 'true');
      slide.toggleAttribute('active', isSelected);
    });

    gallery.querySelectorAll('slideshow-controls[thumbnails] .slideshow-controls__thumbnail').forEach((thumbnail, index) => {
      const isSelected = index === selectedIndex;
      thumbnail.setAttribute('aria-current', isSelected ? 'true' : 'false');
      thumbnail.toggleAttribute('active', isSelected);
    });
  }

  /**
   * Re-applies the selected media after late variant/gallery rendering settles.
   * @param {Element} gallery
   * @param {string} mediaId
   */
  #scheduleMediaSelection(gallery, mediaId) {
    const target = gallery.closest('.shopify-section, dialog') || document;
    const requestId = `${Date.now()}-${++this.#mediaSelectionRequest}`;
    target.__allbetterMediaSelectionRequest = requestId;
    const isCurrentSelection = () => {
      if (target.__allbetterMediaSelectionRequest !== requestId) return false;

      const variantJson = target.querySelector('variant-picker script[type="application/json"]')?.textContent;
      if (!variantJson) return true;

      try {
        const variant = JSON.parse(variantJson);
        const currentMediaId = variant?.featured_media?.id;
        return !currentMediaId || String(currentMediaId) === mediaId;
      } catch (error) {
        return true;
      }
    };
    const applyMedia = () => {
      if (!isCurrentSelection()) return;
      gallery.selectMediaById?.(mediaId);
      this.#syncGalleryElementById(gallery, mediaId);
    };

    requestAnimationFrame(applyMedia);
    [150, 750, 1800, 3600, 6200].forEach((delay) => {
      window.setTimeout(applyMedia, delay);
    });
  }

  /**
   * Aligns the gallery with the variant selected in the picker on first load.
   */
  #selectInitialVariantMedia() {
    const target = this.closest('.shopify-section, dialog') || document;
    const variantJson = target.querySelector('variant-picker script[type="application/json"]')?.textContent;
    if (!variantJson) return;

    try {
      const variant = JSON.parse(variantJson);
      const mediaId = variant?.featured_media?.id;
      if (mediaId) this.#selectMediaById(String(mediaId));
    } catch (error) {
      // Ignore malformed variant JSON and keep the merchant-defined first image.
    }
  }

  /**
   * Removes imported swatch/thumbnail files from the main product gallery.
   * Those files can be attached as variant media but are too small for the gallery.
   */
  #removeLowQualityGalleryMedia() {
    const removedSlideIndexes = new Set();

    this.querySelectorAll('slideshow-slide').forEach((slide, index) => {
      if (!this.#elementHasLowQualityImage(slide)) return;

      removedSlideIndexes.add(index);
      slide.remove();
    });

    if (removedSlideIndexes.size) {
      this.querySelectorAll('slideshow-controls[thumbnails]').forEach((controls) => {
        controls
          .querySelectorAll('.slideshow-controls__thumbnail')
          .forEach((thumbnail, index) => {
            if (removedSlideIndexes.has(index) || this.#elementHasLowQualityImage(thumbnail)) thumbnail.remove();
          });
      });
    }

    this.querySelectorAll('[ref="media[]"], .dialog-thumbnails-list__thumbnail').forEach((item) => {
      if (this.#elementHasLowQualityImage(item)) item.remove();
    });

    const firstSlide = this.refs.slideshow?.refs.slides?.[0] || this.querySelector('slideshow-slide');
    firstSlide?.setAttribute('aria-hidden', 'false');
  }

  /**
   * @param {string} mediaId
   * @returns {boolean}
   */
  #isLowQualityMediaId(mediaId) {
    const safeMediaId = globalThis.CSS?.escape ? CSS.escape(mediaId) : mediaId.replaceAll('"', '\\"');
    const media = this.querySelector(`[data-media-id="${safeMediaId}"], slideshow-slide[slide-id="${safeMediaId}"]`);

    return this.#elementHasLowQualityImage(media);
  }

  /**
   * @param {Element | null | undefined} element
   * @returns {boolean}
   */
  #elementHasLowQualityImage(element) {
    if (!element) return false;

    const src = [...element.querySelectorAll('img')]
      .map((image) => image.currentSrc || image.src || image.getAttribute('src') || image.getAttribute('srcset') || '')
      .join(' ')
      .toLowerCase();

    return src.includes('_ss64') || src.includes('_ac_sy550');
  }

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
